/**
 * Coaching sessions (COACHING-SESSIONS) — debriefs recorded after the
 * procedure, uploaded as attachments of kind `coaching`.
 *
 * They are on their own timeline, so they never join the camera angles in the
 * procedure's media strip. Instead the trace card offers a transcript source
 * picker: the procedure recording (the default), or one of the coaching
 * sessions, which swaps in its own player and its full transcript. Clicking a
 * line plays from there; the line being spoken is highlighted as it plays.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import {
  listAttachmentsForMySession,
  listAttachmentsForTenantSession,
  retranscribeCoachingSession,
  type SessionAttachment,
} from '../api/sessionAttachments';
import { extractErrorMessage } from '../api/errors';
import { CaptionTrack, type CaptionEntry } from './CaptionTrack';
import { MomentInspector } from './MomentInspector';

/** How often to look again while a transcript is still being made. */
const POLL_MS = 15_000;

function inProgress(a: SessionAttachment): boolean {
  return a.transcript_status === 'pending' || a.transcript_status === 'running';
}

/**
 * The session's uploaded coaching sessions, oldest first. Re-fetched while any
 * transcript is still in progress, so it appears without a reload.
 */
export function useCoachingSessions(
  tenantId: string | null,
  sessionRowId: string | null,
): {
  sessions: SessionAttachment[];
  /** A recorded debrief's extra cameras, by the main recording's id. */
  angles: Record<string, SessionAttachment[]>;
  reload: () => void;
} {
  const [all, setAll] = useState<SessionAttachment[]>([]);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!sessionRowId) {
      setAll([]);
      return;
    }
    let live = true;
    (tenantId
      ? listAttachmentsForTenantSession(tenantId, sessionRowId)
      : listAttachmentsForMySession(sessionRowId)
    )
      .then((rows) => {
        if (!live) return;
        setAll(
          rows
            .filter((r) => r.kind === 'coaching' && r.uploaded_at)
            .sort((a, b) => a.created_at.localeCompare(b.created_at)),
        );
      })
      .catch(() => live && setAll([]));
    return () => {
      live = false;
    };
  }, [tenantId, sessionRowId, tick]);

  // DEBRIEF-RECORDING: an extra camera of a recorded debrief is an angle of
  // its main recording, not a coaching session of its own.
  const sessions = useMemo(() => all.filter((a) => !a.coaching_parent_id), [all]);
  const angles = useMemo(() => {
    const out: Record<string, SessionAttachment[]> = {};
    for (const a of all) {
      if (a.coaching_parent_id) (out[a.coaching_parent_id] ??= []).push(a);
    }
    return out;
  }, [all]);

  const waiting = sessions.some(inProgress);
  useEffect(() => {
    if (!waiting) return;
    const id = window.setTimeout(reload, POLL_MS);
    return () => window.clearTimeout(id);
  }, [waiting, sessions, reload]);

  return { sessions, angles, reload };
}

function sourceLabel(a: SessionAttachment): string {
  const when = new Date(a.created_at).toLocaleDateString();
  return `Coaching · ${a.original_filename} (${when})`;
}

/** Procedure recording vs. each coaching session. Hidden when there are none. */
export function TranscriptSourcePicker({
  sessions,
  value,
  onChange,
}: {
  sessions: SessionAttachment[];
  value: string;
  onChange: (value: string) => void;
}) {
  if (sessions.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography variant="body2" color="text.secondary">
        Transcript
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(_, v: string | null) => v && onChange(v)}
        aria-label="Transcript source"
      >
        <ToggleButton value="procedure">Procedure recording</ToggleButton>
        {sessions.map((a) => (
          <ToggleButton key={a.id} value={a.id}>
            {sourceLabel(a)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

/** One coaching session: its player beside its full transcript. */
export function CoachingSessionView({
  session,
  angles = [],
  tenantId,
  onChanged,
}: {
  session: SessionAttachment;
  /** The same debrief from other cameras (DEBRIEF-RECORDING). */
  angles?: SessionAttachment[];
  /** Present on the tenant surface — enables "Transcribe again". */
  tenantId: string | null;
  onChanged: () => void;
}) {
  const mediaRef = useRef<HTMLVideoElement & HTMLAudioElement>(null);
  const [now, setNow] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entries: CaptionEntry[] = useMemo(
    () =>
      (session.transcript ?? []).map((s) => ({
        t: s.start_s,
        headline: '',
        transcript: s.text,
        summary: '',
      })),
    [session.transcript],
  );

  const playFrom = (t: number) => {
    const el = mediaRef.current;
    if (!el) return;
    el.currentTime = t;
    void el.play().catch(() => undefined);
    setNow(t);
  };

  const retranscribe = async () => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      await retranscribeCoachingSession(tenantId, session.id);
      onChanged();
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not start the transcript'));
    } finally {
      setBusy(false);
    }
  };

  // Another camera of the same debrief plays the same conversation: switch
  // picture, keep the transcript and the moment.
  const [angle, setAngle] = useState(0);
  const cameras = [session, ...angles];
  const shown = cameras[Math.min(angle, cameras.length - 1)];
  const switchAngle = (i: number) => {
    const t = mediaRef.current?.currentTime ?? 0;
    setAngle(i);
    window.setTimeout(() => {
      if (mediaRef.current) mediaRef.current.currentTime = t;
    }, 0);
  };
  const isVideo = shown.content_type.startsWith('video/');
  const url = shown.presigned_get_url ?? undefined;
  const media = isVideo ? (
    <Box
      component="video"
      ref={mediaRef}
      src={url}
      controls
      preload="metadata"
      aria-label={`Coaching session ${session.original_filename}`}
      onTimeUpdate={(e) => setNow((e.target as HTMLVideoElement).currentTime)}
      sx={{ width: '100%', maxHeight: 300, bgcolor: 'common.black', borderRadius: 1 }}
    />
  ) : (
    <Box
      component="audio"
      ref={mediaRef}
      src={url}
      controls
      preload="metadata"
      aria-label={`Coaching session ${session.original_filename}`}
      onTimeUpdate={(e) => setNow((e.target as HTMLAudioElement).currentTime)}
      sx={{ width: '100%' }}
    />
  );

  const status = session.transcript_status;
  let aside;
  if (status === 'pending' || status === 'running') {
    aside = (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Transcribing the coaching session — this takes a few minutes. It
          appears here when it's ready.
        </Typography>
        <LinearProgress />
      </Box>
    );
  } else if (status === 'failed' || status === 'skipped') {
    aside = (
      <Alert
        severity={status === 'failed' ? 'error' : 'info'}
        action={
          tenantId ? (
            <Button color="inherit" size="small" disabled={busy} onClick={() => void retranscribe()}>
              {status === 'failed' ? 'Try again' : 'Transcribe'}
            </Button>
          ) : undefined
        }
      >
        {session.transcript_detail ??
          (status === 'failed' ? 'The transcript could not be made.' : 'Not transcribed.')}
      </Alert>
    );
  } else if (entries.length === 0) {
    aside = (
      <Typography variant="body2" color="text.secondary">
        {session.transcript_detail ?? 'No speech was found in this recording.'}
      </Typography>
    );
  } else {
    aside = <CaptionTrack entries={entries} pinnedT={now} onPick={playFrom} fill />;
  }

  return (
    <Stack spacing={1}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <MomentInspector
        media={
          cameras.length > 1 ? (
            <Stack spacing={1}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={angle}
                onChange={(_, v: number | null) => v !== null && switchAngle(v)}
                aria-label="Camera"
              >
                {cameras.map((c, i) => (
                  <ToggleButton key={c.id} value={i}>
                    Camera {i + 1}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {media}
            </Stack>
          ) : (
            media
          )
        }
        aside={aside}
      />
    </Stack>
  );
}
